import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { isEntitlementLogEnabled } from 'feature-flags';
import { di } from 'react-magnetic-di';
import { fetchQuery, graphql } from 'react-relay';

import ErrorBoundary from 'view/error-boundary/error-boundary';
import { ForgeCustomFieldEdit } from 'view/forge-ui/custom-field';
import { getExtensionIdFromLegacyFieldType, isForgeLegacyFieldType } from 'view/forge-ui/custom-field/utils';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FieldBase } from '@atlassian/help-center-common-component/fields/field-base';
import type { FieldFormProps } from '@atlassian/help-center-common-component/fields/types';
import { CUSTOM_FIELD, CUSTOM_FIELD_TYPE } from '@atlassian/help-center-common-component/forge-ui/constants';
import type {
    CustomField,
    CustomFieldType,
    CustomFieldExtensionData,
    CustomFieldExtensionPayload,
} from '@atlassian/help-center-common-component/forge-ui/types';
import { getCloudId } from '@atlassian/help-center-common-util/meta';
import environment from '@atlassian/help-center-common-util/relay/environment';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import type { forgeFieldQuery, forgeFieldQuery$data } from './__generated__/forgeFieldQuery.graphql';
import { getMappedFieldType } from './utils';
import type { FieldType } from 'state/persisted/request-create/types';

/* eslint-disable relay/unused-fields */
export const FORGE_FIELD_QUERY = graphql`
    query forgeFieldQuery(
        $cloudId: ID!
        $context: JiraExtensionRenderingContextInput!
        $types: [String!]!
        $includeHidden: Boolean!
    ) {
        jira {
            forge {
                extensions(cloudId: $cloudId, context: $context, types: $types, includeHidden: $includeHidden) {
                    hiddenBy
                    type
                    id
                    environmentId
                    environmentKey
                    environmentType
                    installationId
                    appVersion
                    consentUrl
                    properties
                    userAccess {
                        hasAccess
                    }
                    egress {
                        addresses
                        type
                    }
                    license {
                        active
                        type
                        supportEntitlementNumber
                        trialEndDate
                        subscriptionEndDate
                        isEvaluation
                        billingPeriod
                        ccpEntitlementId
                        ccpEntitlementSlug
                        capabilitySet
                    }
                }
            }
        }
    }
`;
/* eslint-enable relay/unused-fields */

export interface Props extends FieldFormProps<string> {
    entryPoint: 'view' | 'edit';
    value?: string;
    jiraFieldType: string;
    disabled: boolean; // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2153
    portalId: number;
    requestTypeId: number;
    enableSubmitButton: () => void;
    disableSubmitButton: () => void;
    fallbackRender: (newType: FieldType) => JSX.Element;
}

export interface ForgeCustomFieldProps extends Props {
    extension: CustomField | CustomFieldType;
}

export interface ForgeFieldContainerProps {
    id: Props['id'];
    label: Props['label'];
    required: Props['required'];
    description: Props['description'];
    errorMessage: Props['errorMessage'];
    children: React.ReactNode;
}

const ForgeFieldContainer: React.FC<ForgeFieldContainerProps> = ({
    id,
    label,
    required,
    description,
    errorMessage,
    children,
}) => {
    return (
        <FieldBase
            htmlFor={id}
            label={label}
            required={required}
            description={description}
            errorMessage={errorMessage}
            labelId={`${id}-label`}
        >
            {children}
        </FieldBase>
    );
};

const ForgeFieldImpl: React.FC<ForgeCustomFieldProps> = ({
    id,
    value,
    extension,
    requestTypeId,
    portalId,
    entryPoint,
    fallbackRender,
}) => {
    const extensionData: CustomFieldExtensionData = useMemo(
        () => ({
            fieldType: extension.id,
            fieldId: id,
            type: extension.type,
            portal: {
                id: portalId,
            },
            request: {
                typeId: requestTypeId,
            },
            ...(entryPoint === 'edit'
                ? {
                      renderContext: 'portal-request',
                      experience: 'portal-request',
                      entryPoint: 'edit',
                  }
                : {
                      renderContext: 'portal-view',
                      experience: 'portal-view',
                      entryPoint: 'view',
                  }),
        }),
        [extension.id, extension.type, id, portalId, requestTypeId, entryPoint]
    );

    const extensionPayload: CustomFieldExtensionPayload = useMemo(() => ({ fieldValue: value }), [value]);

    const nativeFieldNode = useMemo(() => {
        const hasEntryPointProperty = entryPoint in extension.properties;
        const hasResourceProperty = hasEntryPointProperty && 'resource' in (extension.properties[entryPoint] || {});
        return hasResourceProperty ? null : fallbackRender(getMappedFieldType(extension));
    }, [entryPoint, extension, fallbackRender]);

    if (nativeFieldNode) {
        return nativeFieldNode;
    }

    if (entryPoint === 'edit') {
        return (
            <ForgeCustomFieldEdit
                extension={extension}
                extensionData={extensionData}
                extensionPayload={extensionPayload}
            />
        );
    }

    return null;
};

const ForgeFieldMain = (props: Props) => {
    di(fetchQuery, getCloudId, useAnalyticsEvents, reportError, isEntitlementLogEnabled);

    const { disableSubmitButton, enableSubmitButton } = props;
    const cloudId = getCloudId();
    const [shouldRenderContainer, setShouldRenderContainer] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [fields, setFields] = useState<(CustomField | CustomFieldType)[]>([]);
    const [error, setError] = useState(false);

    const fetchForgeFields = useCallback(() => {
        setIsLoading(true);
        disableSubmitButton();
        fetchQuery<forgeFieldQuery>(environment, FORGE_FIELD_QUERY, {
            cloudId,
            context: {}, // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2166
            types: [CUSTOM_FIELD, CUSTOM_FIELD_TYPE],
            includeHidden: true,
        })
            .toPromise()
            .then((data: forgeFieldQuery$data) => {
                const extensions = data?.jira?.forge?.extensions || [];
                const customFields = extensions.filter(
                    (ext) => ext.type === CUSTOM_FIELD || ext.type === CUSTOM_FIELD_TYPE
                ) as unknown as (CustomField | CustomFieldType)[]; // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2147
                setFields(customFields);
            })
            .catch(() => {
                setFields([]);
                setError(true);
            })
            .finally(() => {
                setIsLoading(false);
                enableSubmitButton();
            });
    }, [cloudId, disableSubmitButton, enableSubmitButton]);

    useEffect(() => {
        fetchForgeFields();
    }, [fetchForgeFields]);

    const fallbackRender = useCallback(
        (newType: FieldType) => {
            setShouldRenderContainer(false);
            return props.fallbackRender(newType);
        },
        [props]
    );

    const extension = useMemo(() => {
        const extensionId = isForgeLegacyFieldType(props.jiraFieldType)
            ? getExtensionIdFromLegacyFieldType(props.jiraFieldType)
            : props.jiraFieldType;
        return fields.find((field) => field.id === extensionId);
    }, [fields, props.jiraFieldType]);

    const ContainerComponent = useMemo(
        () =>
            shouldRenderContainer
                ? ({ children }: { children: React.ReactNode }) => (
                      <ForgeFieldContainer
                          id={props.id}
                          label={props.label}
                          required={props.required}
                          description={props.description}
                          errorMessage={props.errorMessage}
                      >
                          {children}
                      </ForgeFieldContainer>
                  )
                : React.Fragment,
        [props.description, props.errorMessage, props.id, props.label, props.required, shouldRenderContainer]
    );

    if (isLoading) {
        // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2155
        return (
            <ContainerComponent>
                <div>Loading</div>
            </ContainerComponent>
        );
    }

    if (error) {
        // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2156
        return (
            <ContainerComponent>
                <div>Error</div>
            </ContainerComponent>
        );
    }

    if (!extension) {
        // TODO: https://ecosystem-platform.atlassian.net/browse/ARKEN-2157
        return (
            <ContainerComponent>
                <div>Not found</div>
            </ContainerComponent>
        );
    }

    return (
        <ContainerComponent>
            <ForgeFieldImpl {...props} extension={extension} fallbackRender={fallbackRender} />
        </ContainerComponent>
    );
};

const ForgeFieldWithErrorBoundary = (props: Props) => (
    <ErrorBoundary
        id={props.entryPoint === 'view' ? `forge-field-view` : 'forge-field-edit'}
        packageName="portal-fields"
    >
        <ForgeFieldMain {...props} />
    </ErrorBoundary>
);

const selector = createSelector(
    () => null,
    () => ({})
);

export const ForgeField = connect(selector)(ForgeFieldWithErrorBoundary);
